import { useState, useEffect, useRef, FormEvent } from 'react';
import { Modal, Button } from '@ai-tutor/ui';
import { TagInput } from './TagInput';

interface FolderFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: { name: string; tags: string[] }) => Promise<void> | void;
  /** If provided, fields are seeded (rename/edit mode). Omit for create. */
  initial?: { name: string; tags: string[] };
  title: string;
  submitLabel?: string;
}

export function FolderFormModal({
  isOpen,
  onClose,
  onSubmit,
  initial,
  title,
  submitLabel = 'Save',
}: FolderFormModalProps) {
  const [name, setName] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setName(initial?.name ?? '');
      setTags(initial?.tags ?? []);
      setSubmitting(false);
      // Focus the name input on open without the JSX autoFocus prop
      const id = window.setTimeout(() => inputRef.current?.focus(), 50);
      return () => window.clearTimeout(id);
    }
  }, [isOpen, initial?.name, initial?.tags]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await onSubmit({ name: trimmed, tags });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size='md'>
      <form onSubmit={handleSubmit} className='space-y-4 px-6 py-5'>
        <div>
          <label
            htmlFor='folder-name'
            className='mb-1.5 block text-sm font-medium text-foreground'
          >
            Name
          </label>
          <input
            ref={inputRef}
            id='folder-name'
            type='text'
            value={name}
            onChange={e => setName(e.target.value)}
            required
            maxLength={200}
            placeholder='e.g. Physics'
            // 44px and 16px type on a phone (iOS zooms into smaller fields).
            className='w-full rounded-lg border border-border bg-card px-3 py-2 text-base text-foreground outline-none placeholder:text-muted-foreground focus:border-brand-400 focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 dark:focus:border-primary max-sm:h-11 sm:text-sm'
          />
        </div>

        <div>
          <label className='mb-1.5 block text-sm font-medium text-foreground'>
            Tags
          </label>
          <TagInput
            value={tags}
            onChange={setTags}
            placeholder='Add a tag and press Enter'
          />
        </div>

        <div className='flex justify-end gap-2 pt-2 max-sm:[&>button]:h-11 max-sm:[&>button]:flex-1'>
          <Button type='button' variant='ghost' onClick={onClose}>
            Cancel
          </Button>
          <Button type='submit' disabled={!name.trim() || submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
